import Phaser from 'phaser';
import { setSoundPaused } from '../audio/Sfx';
import { CELL_W, PIXEL_FONT } from './pixelFont';

const DEPTH = 1100;
const CHIP = 16;
const BUTTON_W = 150;
const BUTTON_H = 28;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const inside = (b: Box, x: number, y: number) =>
  x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;

/**
 * The pause chip in the top-right corner (GDD: HUD) and the menu it opens: RESUME, or REWIND TO
 * LAB (giving up the run). Esc or P toggles it on a keyboard, and it opens by itself when the game
 * is hidden (a call, the home screen), so a run is never lost in the background. While paused the
 * scene stops stepping its simulation, its clock and tweens stop, the sound stops, and `onChange`
 * lets it turn its controls off.
 */
export class PauseMenu {
  paused = false;
  private readonly chip: Phaser.GameObjects.Graphics;
  private readonly overlay: Phaser.GameObjects.Graphics;
  private readonly texts: Phaser.GameObjects.BitmapText[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly handlers: { onRewind: () => void; onChange: (paused: boolean) => void },
  ) {
    this.chip = scene.add.graphics().setDepth(DEPTH);
    this.overlay = scene.add.graphics().setDepth(DEPTH + 1);
    this.drawChip();

    // Taps are read here rather than by interactive objects, so the chip works whatever the
    // touch controls are doing with the same finger.
    const onDown = (p: Phaser.Input.Pointer) => {
      if (this.paused) {
        const { resume, rewind } = this.buttons();
        if (inside(resume, p.x, p.y)) this.resume();
        else if (inside(rewind, p.x, p.y)) this.rewind();
      } else if (inside(this.chipBox(), p.x, p.y)) {
        this.pause();
      }
    };
    scene.input.on('pointerdown', onDown);
    const kb = scene.input.keyboard;
    const toggle = (e: KeyboardEvent) => {
      if (!e.repeat) this.toggle();
    };
    kb?.on('keydown-ESC', toggle);
    kb?.on('keydown-P', toggle);
    const onHidden = () => this.pause();
    scene.game.events.on(Phaser.Core.Events.HIDDEN, onHidden);
    scene.events.once('shutdown', () => {
      scene.input.off('pointerdown', onDown);
      scene.game.events.off(Phaser.Core.Events.HIDDEN, onHidden);
      if (this.paused) setSoundPaused(scene, false);
    });
  }

  toggle(): void {
    if (this.paused) this.resume();
    else this.pause();
  }

  pause(): void {
    if (!this.paused) this.set(true);
  }

  resume(): void {
    if (this.paused) this.set(false);
  }

  /** Lays the chip and the menu out again for the screen's size. */
  resize(): void {
    this.drawChip();
    this.draw();
  }

  /** Where the chip and the buttons are, in game pixels (for tools/e2e to tap them). */
  layout(): { chip: Box; resume: Box; rewind: Box } {
    return { chip: this.chipBox(), ...this.buttons() };
  }

  private rewind(): void {
    this.set(false);
    this.handlers.onRewind();
  }

  private set(paused: boolean): void {
    this.paused = paused;
    this.scene.time.paused = paused;
    if (paused) this.scene.tweens.pauseAll();
    else this.scene.tweens.resumeAll();
    setSoundPaused(this.scene, paused);
    this.handlers.onChange(paused);
    this.draw();
  }

  private chipBox(): Box {
    // A little larger than drawn: easy to hit with a thumb.
    return { x: this.scene.scale.width - CHIP - 10, y: 0, w: CHIP + 10, h: CHIP + 8 };
  }

  private buttons(): { resume: Box; rewind: Box } {
    const x = Math.round((this.scene.scale.width - BUTTON_W) / 2);
    const mid = Math.round(this.scene.scale.height / 2);
    return {
      resume: { x, y: mid - 14, w: BUTTON_W, h: BUTTON_H },
      rewind: { x, y: mid + 22, w: BUTTON_W, h: BUTTON_H },
    };
  }

  /** Two bars in a rounded-off square. */
  private drawChip(): void {
    const x = this.scene.scale.width - CHIP - 4;
    const y = 4;
    this.chip
      .clear()
      .fillStyle(0x0b0820, 0.7)
      .fillRect(x, y, CHIP, CHIP)
      .lineStyle(1, 0x46557e, 1)
      .strokeRect(x + 0.5, y + 0.5, CHIP - 1, CHIP - 1)
      .fillStyle(0xffffff, 0.9)
      .fillRect(x + 5, y + 4, 2, 8)
      .fillRect(x + 9, y + 4, 2, 8);
  }

  private draw(): void {
    for (const t of this.texts) t.destroy();
    this.texts.length = 0;
    const g = this.overlay.clear();
    if (!this.paused) return;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    g.fillStyle(0x07051a, 0.75).fillRect(0, 0, w, h);
    this.text(w / 2, h / 2 - 44, 'PAUSED', 2, 0xffd23f);
    const { resume, rewind } = this.buttons();
    this.button(resume, 'RESUME', 0x2f7f86, 0x7df9ff);
    this.button(rewind, 'REWIND TO LAB', 0x1d2540, 0x46557e);
  }

  private button(b: Box, label: string, fill: number, line: number): void {
    this.overlay
      .fillStyle(fill, 1)
      .fillRect(b.x, b.y, b.w, b.h)
      .lineStyle(1, line, 1)
      .strokeRect(b.x + 0.5, b.y + 0.5, b.w - 1, b.h - 1);
    this.text(b.x + b.w / 2, b.y + b.h / 2, label, 1, 0xffffff);
  }

  private text(x: number, y: number, s: string, scale: number, tint: number): void {
    this.texts.push(
      this.scene.add
        .bitmapText(Math.round(x), Math.round(y), PIXEL_FONT, s, CELL_W * scale)
        .setOrigin(0.5)
        .setTint(tint)
        .setDepth(DEPTH + 2),
    );
  }
}
